import { useEffect, useState } from "react";
import { useContracts } from "../useHook";
import {
  EyeIcon,
  TrashIcon,
  DocumentTextIcon,
  PencilSquareIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
} from "@heroicons/react/24/outline";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import GlobalPagination from "../../../Components/Pagination/GlobalPagination";
import Loader from "../../../Utils/loader";
import {
  usePermission,
  SystemPermissions,
} from "../../../Utils/permissionAccess";
import { UrduInput } from "../../../Components/Shared/UrduInput";

const DESCRIPTION_MAX_WORDS = 100;

const countWords = (text) => {
  if (!text) return 0;
  return text.trim().split(/\s+/).filter(Boolean).length;
};


function Contracts() {
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const canCreate = hasPermission(SystemPermissions.Contract.CREATE);
  const canUpdate = hasPermission(SystemPermissions.Contract.UPDATE);
  const canDelete = hasPermission(SystemPermissions.Contract.DELETE);
  const canViewMake = hasPermission(SystemPermissions.Contract.VIEW_MAKE);
  const canViewUserPdf = hasPermission(SystemPermissions.Contract.VIEW_USER_PDF);
  const canShowActions = canViewMake || canUpdate || canViewUserPdf || canDelete;
  const {
    createContract,
    getContracts,
    deleteContract,
    updateContract,
    getAllContractCategories
  } = useContracts();

  const [contracts, setContracts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [updating, setUpdating] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const [offset, setOffset] = useState(0);
  const [limit] = useState(9);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");

  const [file, setFile] = useState(null);
  const [fileUr, setFileUr] = useState(null);
  const [title, setTitle] = useState("");
  const [contractTitle_ur, setContractTitleUr] = useState("");
  const [customUrl, setCustomUrl] = useState("");
  const [description, setDescription] = useState("");
  const [description_ur, setDescriptionUr] = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState("");

  const [editId, setEditId] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editContractTitleUr, setEditContractTitleUr] = useState("");
  const [editCustomUrl, setEditCustomUrl] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editDescriptionUr, setEditDescriptionUr] = useState("");
  const [editCategoryId, setEditCategoryId] = useState("");

  const fetchContracts = async (currentOffset = offset) => {
    try {
      setLoading(true);
      const params = {};
      if (search) params.search = search;
      if (categoryFilter) params.categoryId = categoryFilter;

      const res = await getContracts(currentOffset, limit, params);
      setContracts(res.data || []);
      setTotal(res.pagination?.totalRecords || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await getAllContractCategories();
      setCategories(res || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchContracts();
    fetchCategories();
  }, [offset]);

  const handlePageChange = (newOffset) => {
    setOffset(newOffset);
  };

  const handleSearch = () => {
    setOffset(0);
    fetchContracts(0);
  };

  const handleReset = () => {
    setSearch("");
    setCategoryFilter("");
    setOffset(0);
    fetchContracts(0);
  };

  const handleUpload = async () => {
    if (!file || !title || !selectedCategoryId || !customUrl.trim()) return;
    if (countWords(description) > DESCRIPTION_MAX_WORDS) return;

    const formData = new FormData();
    formData.append("title", title);
    if (contractTitle_ur) formData.append("contractTitle_ur", contractTitle_ur);
    formData.append("description", description);
    if (description_ur) formData.append("description_ur", description_ur);
    formData.append("customUrl", customUrl.trim());
    formData.append("file", file);
    if (fileUr) formData.append("file_ur", fileUr);
    formData.append("contract_category_id", selectedCategoryId);

    try {
      setUploading(true);
      await createContract(formData);

      setFile(null);
      setFileUr(null);
      setTitle("");
      setContractTitleUr("");
      setCustomUrl("");
      setDescription("");
      setDescriptionUr("");
      setSelectedCategoryId("");
      setIsModalOpen(false);
      fetchContracts(0);

      Swal.fire("Success", "Contract uploaded successfully", "success");
    } catch (err) {
      console.error(err);
      Swal.fire("Error", err.response?.data?.message || "Upload failed", "error");
    } finally {
      setUploading(false);
    }
  };

  const openEditModal = (contract) => {
    setEditId(contract?.id || null);
    setEditTitle(contract?.title || "");
    setEditContractTitleUr(contract?.contractTitle_ur || "");
    setEditCustomUrl(contract?.customUrl || "");
    setEditDescription(contract?.description || "");
    setEditDescriptionUr(contract?.description_ur || "");
    setEditCategoryId(contract?.contract_category_id || "");
    setIsEditModalOpen(true);
  };

  const handleUpdate = async () => {
    if (!editId || !editTitle || !editCategoryId || !editCustomUrl.trim()) return;
    if (countWords(editDescription) > DESCRIPTION_MAX_WORDS) return;

    try {
      setUpdating(true);
      await updateContract(editId, {
        title: editTitle,
        contractTitle_ur: editContractTitleUr,
        description: editDescription,
        description_ur: editDescriptionUr,
        customUrl: editCustomUrl.trim(),
        contract_category_id: editCategoryId
      });
      setIsEditModalOpen(false);
      setEditId(null);
      setEditTitle("");
      setEditContractTitleUr("");
      setEditCustomUrl("");
      setEditDescription("");
      setEditDescriptionUr("");
      setEditCategoryId("");
      fetchContracts();
      Swal.fire("Success", "Contract updated successfully", "success");
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Update failed", "error");
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to recover this contract!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        await deleteContract(id);
        fetchContracts();

        Swal.fire("Deleted!", "Contract has been deleted.", "success");
      } catch (err) {
        console.error(err);
        Swal.fire("Error", "Delete failed", "error");
      }
    }
  };

  const handleView = (id) => {
    navigate(`/contracts/view/${id}`);
  };

  return (
    <div>
      <div className="bg-gray-100 p-4 flex flex-wrap justify-between items-center gap-3 mb-6">
        <h2 className="text-2xl font-bold">Contract Management</h2>

        {canCreate && (
          <button
            onClick={() => setIsModalOpen(true)}
            className="btn btn-primary h-[44px]"
          >
            Add Contract
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white p-5 w-full rounded-lg pagination-shadow flex flex-wrap xlCustom:flex-nowrap xlCustom:justify-end items-center gap-3 my-5">
        <div className="search-wrapper">
          <input
            type="text"
            className="search-input"
            placeholder="Search contracts..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <MagnifyingGlassIcon className="search-icon h-5 w-5" />
        </div>

        <div className="min-w-[200px]">
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="w-full px-4 py-2 border rounded-lg outline-none transition h-[44px]"
          >
            <option value="">All Categories</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.title}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-wrap gap-2">
          <button onClick={handleSearch} className="btn btn-filter filter-btn">
            <FunnelIcon className="h-4 w-4 text-white" /> Filter
          </button>
          <button onClick={handleReset} className="btn btn-reset-filter filter-btn">
            Reset
          </button>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <Loader message="Loading contracts..." fullScreen={false} />
      ) : contracts.length === 0 ? (
        <div className="bg-white p-12 rounded-2xl border border-dashed border-gray-300 text-center text-gray-500">
          No contracts found matching your criteria.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {contracts.map((contract) => (
            <div
              key={contract.id}
              className="bg-white p-5 rounded-2xl shadow-md hover:shadow-lg transition duration-200 border border-gray-100 flex flex-col"
            >
              <div className="flex-1">
                <div className="flex justify-between items-start mb-2">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-blue-600 bg-blue-50 px-2 py-1 rounded">
                    {contract.category?.title || "Uncategorized"}
                  </span>
                </div>
                <h3 className="font-semibold text-lg text-gray-800 mb-2 truncate">
                  {contract.title}
                </h3>
                {!!contract.description && (
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2 italic">
                    {contract.description}
                  </p>
                )}
              </div>

              {canShowActions && (
                <div className="flex gap-2 flex-wrap mt-4 pt-4 border-t border-gray-50">
                  {canViewMake && (
                    <button
                      onClick={() => handleView(contract.id)}
                      className="btn btn-view text-white"
                    >
                      <EyeIcon className="h-4 w-4 text-white" />
                      View
                    </button>
                  )}

                  {canUpdate && (
                    <button
                      onClick={() => openEditModal(contract)}
                      className="btn btn-edit text-white"
                    >
                      <PencilSquareIcon className="h-4 w-4 text-white" />
                      Edit
                    </button>
                  )}

                  {canViewUserPdf && (
                    <button
                      onClick={() =>
                        navigate(`/contracts/user-contracts/${contract.id}`)
                      }
                      className="btn btn-action"
                    >
                      <DocumentTextIcon className="h-4 w-4 text-white" />
                      PDFs
                    </button>
                  )}

                  {canDelete && (
                    <button
                      onClick={() => handleDelete(contract.id)}
                      className="btn btn-delete text-white"
                    >
                      <TrashIcon className="h-4 w-4 text-white" />
                      Delete
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {!loading && total > limit && (
        <div className="mt-10">
          <GlobalPagination
            offset={offset}
            limit={limit}
            total={total}
            onPageChange={handlePageChange}
          />
        </div>
      )}

      {/* Modal */}
      {canCreate && isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50 p-4">
          <div className="bg-white w-full max-w-[500px] p-8 rounded-3xl shadow-2xl relative overflow-y-auto max-h-[95vh]">
            <button
              onClick={() => setIsModalOpen(false)}
              className="absolute top-5 right-5 text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
            </button>

            <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">
              New Contract
            </h3>

            <div className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Contract Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. Tenancy Agreement"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition"
                />
                <UrduInput
                  label="کنٹریکٹ کا عنوان"
                  name="contractTitle_ur"
                  value={contractTitle_ur}
                  onChange={(e) => setContractTitleUr(e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Contract URL <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. tenancy-agreement"
                  value={customUrl}
                  onChange={(e) => setCustomUrl(e.target.value)}
                  className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Select Category <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedCategoryId}
                  onChange={(e) => setSelectedCategoryId(e.target.value)}
                  className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition"
                >
                  <option value="">Choose Category</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.title}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  placeholder="Summary of the contract..."
                  value={description}
                  onChange={(e) => {
                    const val = e.target.value;
                    const wordMatches = [...val.matchAll(/\S+/g)];
                    if (wordMatches.length <= DESCRIPTION_MAX_WORDS) {
                      setDescription(val);
                    }
                  }}
                  className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition min-h-[100px]"
                />
                <div className="flex justify-between mt-1 px-1">
                  <p className="text-[10px] text-gray-400 uppercase font-bold tracking-wider">Word count limit</p>
                  <p className={`text-[10px] font-bold ${countWords(description) >= DESCRIPTION_MAX_WORDS ? "text-red-500" : "text-gray-400"}`}>
                    {countWords(description)}/{DESCRIPTION_MAX_WORDS}
                  </p>
                </div>
                <UrduInput
                  label="تفصیل"
                  name="description_ur"
                  value={description_ur}
                  onChange={(e) => setDescriptionUr(e.target.value)}
                  type="textarea"
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Upload Word Template (.docx) <span className="text-red-500">*</span>
                </label>
                <label
                  htmlFor="file-upload"
                  className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-200 rounded-2xl cursor-pointer hover:bg-blue-50 hover:border-blue-300 transition group"
                >
                  <DocumentTextIcon className="w-8 h-8 text-gray-300 group-hover:text-blue-400 mb-2" />
                  <p className="text-gray-500 text-xs font-medium">
                    {file ? file.name : "Select or drop file"}
                  </p>
                  <input
                    id="file-upload"
                    type="file"
                    accept=".doc,.docx"
                    onChange={(e) => setFile(e.target.files[0])}
                    className="hidden"
                  />
                </label>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Upload Urdu Word Template (.docx)
                </label>
                <label
                  htmlFor="file-ur-upload"
                  className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-200 rounded-2xl cursor-pointer hover:bg-blue-50 hover:border-blue-300 transition group"
                >
                  <DocumentTextIcon className="w-8 h-8 text-gray-300 group-hover:text-blue-400 mb-2" />
                  <p className="text-gray-500 text-xs font-medium" dir="rtl">
                    {fileUr ? fileUr.name : "اردو ورڈ فائل منتخب کریں"}
                  </p>
                  <input
                    id="file-ur-upload"
                    type="file"
                    accept=".doc,.docx"
                    onChange={(e) => setFileUr(e.target.files[0])}
                    className="hidden"
                  />
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setIsModalOpen(false);
                    setFileUr(null);
                  }}
                  className="btn btn-reset-filter justify-center"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleUpload}
                  disabled={uploading || !file || !title || !selectedCategoryId || !customUrl.trim()}
                  className={`btn btn-primary justify-center ${
                    uploading || !file || !title || !selectedCategoryId || !customUrl.trim()
                      ? "opacity-50 cursor-not-allowed"
                      : ""
                  }`}
                >
                  {uploading ? "Uploading..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {canUpdate && isEditModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50 p-4">
          <div className="bg-white w-full max-w-[450px] p-8 rounded-3xl shadow-2xl relative overflow-y-auto max-h-[90vh]">
            <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">
              Edit Contract
            </h3>

            <div className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Contract Title
                </label>
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition"
                />
                <UrduInput
                  label="کنٹریکٹ کا عنوان"
                  name="edit_contractTitle_ur"
                  value={editContractTitleUr}
                  onChange={(e) => setEditContractTitleUr(e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Contract URL <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editCustomUrl}
                  onChange={(e) => setEditCustomUrl(e.target.value)}
                  className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Category <span className="text-red-500">*</span>
                </label>
                <select
                  value={editCategoryId}
                  onChange={(e) => setEditCategoryId(e.target.value)}
                  className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition"
                >
                  <option value="">Choose Category</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.title}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={editDescription}
                  onChange={(e) => {
                    const val = e.target.value;
                    const wordMatches = [...val.matchAll(/\S+/g)];
                    if (wordMatches.length <= DESCRIPTION_MAX_WORDS) {
                      setEditDescription(val);
                    }
                  }}
                  className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition min-h-[100px]"
                />
                <div className="flex justify-end mt-1">
                  <p className={`text-[10px] font-bold ${countWords(editDescription) >= DESCRIPTION_MAX_WORDS ? "text-red-500" : "text-gray-400"}`}>
                    {countWords(editDescription)}/{DESCRIPTION_MAX_WORDS} words
                  </p>
                </div>
                <UrduInput
                  label="تفصیل"
                  name="edit_description_ur"
                  value={editDescriptionUr}
                  onChange={(e) => setEditDescriptionUr(e.target.value)}
                  type="textarea"
                  rows={3}
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setIsEditModalOpen(false)}
                  disabled={updating}
                  className="btn btn-reset-filter justify-center"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleUpdate}
                  disabled={updating || !editTitle || !editCategoryId || !editCustomUrl.trim()}
                  className={`btn btn-edit text-white justify-center ${
                    updating || !editTitle || !editCategoryId || !editCustomUrl.trim()
                      ? "opacity-50 cursor-not-allowed"
                      : ""
                  }`}
                >
                  {updating ? "Updating..." : "Update"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Contracts;
